import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { type Params, trackPageView } from "./gtag.ts";

/**
 * 現在のルートの page_view を 1 回だけ送る (JPTP_CANSEI-1400)。
 *
 * StrictMode は開発時に effect を 2 回実行するため、送信済みパスを ref で覚えて二重送信を防ぐ。
 *
 * @param redactPath pathname から機微情報 (アクセストークン・案件 UUID) を除いたパスを返す。
 *   毎レンダリングで再送されないよう、モジュールトップレベルの関数を渡すこと。
 * @param options.enabled false の間は送らない。page_view にデータ取得後の値
 *   (店舗名など) を載せたい画面で、取得完了まで待つために使う。
 * @param options.params page_view に追加で載せるパラメータ。
 */
export function usePageView(
  redactPath: (pathname: string) => string,
  options?: { enabled?: boolean; params?: Params },
): void {
  const { pathname } = useLocation();
  const enabled = options?.enabled ?? true;
  const sentPath = useRef<string | null>(null);
  // params を依存配列に入れるとオブジェクトの同一性が変わるたびに再送されるため ref で渡す。
  const latestParams = useRef(options?.params);
  latestParams.current = options?.params;

  useEffect(() => {
    if (!enabled || sentPath.current === pathname) return;
    sentPath.current = pathname;
    trackPageView(`${window.location.origin}${redactPath(pathname)}`, latestParams.current);
  }, [pathname, redactPath, enabled]);
}

/**
 * router 直下に置いてルート遷移ごとの page_view を送るコンポーネント。
 *
 * @param shouldSkip 自前で page_view を送る画面 (データ取得後の値を載せる画面) の pathname に
 *   対して true を返す。省略時は全ルートを送る。
 */
export function PageViewTracker({
  redactPath,
  shouldSkip,
}: {
  redactPath: (pathname: string) => string;
  shouldSkip?: (pathname: string) => boolean;
}) {
  const { pathname } = useLocation();
  usePageView(redactPath, { enabled: !shouldSkip?.(pathname) });
  return null;
}
